/* eslint-disable max-len */
import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Autocomplete,
  TextField,
} from '@mui/material';
import InputComLabel from '../../../../../../components/shared/custom_input_label';
import { ContainerFormFormik } from 'components/shared/container_form_formik';
import ButtonForm from 'components/buttons/button-form';
import { useStep6 } from './useStep6';
import { getClearedFieldsForUncheck } from './step6.helpers';

const CURSOS_MEDIO_TECNICO = [
  'Administração',
  'Agricultura',
  'Agrimensura, Geomensura ou Topografia',
  'Agroecologia',
  'Agroindústria',
  'Agropecuária',
  'Alimentos ou Indústria de Alimentos',
  'Ambiental ou Meio Ambiente',
  'Construção Civil, Obras ou Estradas',
  'Contabilidade ou Finanças',
  'Controle de Qualidade',
  'Desenho Técnico',
  'Educação',
  'Elétrica, Eletrotécnica ou Eletrônica',
  'Florestal',
  'Geoprocessamento, Geodésia ou Cartografia',
  'Hidrologia',
  'Industrial',
  'Máquinas ou Mecânica',
  'Mineração',
  'Pecuária',
  'Piscicultura e Aquicultura',
  'Prevenção e combate a incêndios',
  'Química',
  'Saúde',
  'Secretariado',
  'Segurança do Trabalho',
  'Serviço Social',
  'Tecnologia da Informação ou de Computadores',
  'Transporte',
  'Turismo',
  'Veterinária',
  'Zootecnia',
];

const CURSOS_SUPERIOR = [
  'Administração',
  'Agronomia ou Engenharia Agronômica',
  'Arquitetura e Urbanismo',
  'Biblioteconomia ou Arquivologia',
  'Biologia ou Ciências Biológicas',
  'Ciências Contábeis',
  'Ciência da Computação ou Análise de Sistemas',
  'Ciências Sociais',
  'Comunicação Social ou Jornalismo',
  'Direito',
  'Economia ou Ciências Econômicas',
  'Engenharia Agrícola',
  'Engenharia Ambiental',
  'Engenharia Sanitária e Ambiental',
  'Engenharia Civil',
  'Engenharia de Agrimensura e/ou Cartográfica',
  'Engenharia de Alimentos',
  'Engenharia Elétrica',
  'Engenharia Florestal',
  'Engenharia (Outra)',
  'Geografia',
  'Geologia',
  'Geoprocessamento',
  'Gestão Ambiental',
  'Gestão Pública',
  'História',
  'Letras',
  'Medicina Veterinária',
  'Nutrição',
  'Pedagogia',
  'Secretariado Executivo',
  'Serviço Social',
  'Turismo',
];

const TIPOS_VINCULO = [
  'Efetivo',
  'Efetivo com cargo comissionado',
  'Contratado temporariamente',
  'Exclusivamente comissionado',
  'Cedido por outra instituição',
];

const Step6: React.FC = () => {
  const { formulario, mascaraTelefone, updateServidor, podeAvancar, handleAvancar, handleVoltar, setPartialForm, quantidadeValida, regexEmail, stepAtual, totalSteps } = useStep6();

  // Helper to extract grau_escolaridade value consistently
  const getGrauEscolaridadeValue = (servidor: any): string => {
    return Array.isArray(servidor.grau_escolaridade)
      ? (servidor.grau_escolaridade[0] || '')
      : (servidor.grau_escolaridade || '');
  };

  return (
    <>
      <Typography
        variant="h5"
        sx={{
          mt: 4,
          mb: 4,
          fontSize: '24px',
          fontWeight: 400,
          textAlign: 'center'
        }}
      >
        Servidores
      </Typography>
      <ContainerFormFormik title='Quadro de Servidores'>
        <Alert severity="info" sx={{ mb: 3 }}>
          Informe os dados dos servidores/servidoras da Unidade Administrativa.
        </Alert>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }} gap={4} display={'flex'} flexDirection={'column'}>

          {/* Quantidade de Servidores */}
          <InputComLabel
            valor={formulario?.quadro_servidores_secretaria_quantidade?.toString() ?? ''}
            setValor={(valor) => {
              const num = parseInt(valor) || 0;
              setPartialForm('quadro_servidores_secretaria_quantidade', num);
              if (num === 0) {
                setPartialForm('quadro_servidores_secretaria_lista', []);
              }
            }}
            labelTexto='Quantos servidores/servidoras compõem o quadro funcional dessa Unidade Administrativa?'
            placeholder='Digite o número de servidores'
            type='number'
            required
          />

          {/* Formulários dos Servidores */}
          {quantidadeValida && formulario?.quadro_servidores_secretaria_lista?.map((servidor, index) => (
            <Box key={index} sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '16px',
              marginTop: '16px'
            }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                Servidor(a) {index + 1}
              </Typography>

              {/* Nome do(a) Servidor(a) */}
              <InputComLabel
                valor={servidor.nome || ''}
                setValor={(valor) => updateServidor(index, 'nome', valor.toUpperCase())}
                labelTexto='Nome do(a) Servidor(a)'
                placeholder='Nome completo do servidor'
                required
              />


              {/* Grau de Escolaridade */}
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel sx={{ fontWeight: 'bold', color: '#333' }} component="legend" required>
                  Grau de Escolaridade
                </FormLabel>
                <RadioGroup
                  value={getGrauEscolaridadeValue(servidor)}
                  onChange={(e) => {
                    const novoValor = e.target.value;
                    // Build all changes in a single update to avoid race condition
                    const clears = getClearedFieldsForUncheck(novoValor);
                    const servidores = formulario?.quadro_servidores_secretaria_lista ?? [];
                    const updatedServidor: Record<string, any> = { ...servidores[index], grau_escolaridade: novoValor };
                    clears.forEach((field) => { updatedServidor[field] = ''; });
                    const novosServidores = [...servidores];
                    novosServidores[index] = updatedServidor as typeof servidores[number];
                    setPartialForm('quadro_servidores_secretaria_lista', novosServidores);
                  }}
                  sx={{ flexDirection: 'column', display: 'flex' }}
                >
                  <FormControlLabel
                    value="Fundamental"
                    control={<Radio />}
                    label="Fundamental"
                  />
                  <FormControlLabel
                    value="Médio"
                    control={<Radio />}
                    label="Médio"
                  />
                  <FormControlLabel
                    value="Médio técnico"
                    control={<Radio />}
                    label="Médio técnico"
                  />
                  <FormControlLabel
                    value="Superior"
                    control={<Radio />}
                    label="Superior"
                  />
                  <FormControlLabel
                    value="Pós-graduado"
                    control={<Radio />}
                    label="Pós-graduado"
                  />
                </RadioGroup>
              </FormControl>

              {/* Lista condicional para Médio Técnico */}
              {getGrauEscolaridadeValue(servidor) === 'Médio técnico' && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel sx={{ color: '#333' }} required>Curso Médio Técnico</FormLabel>
                  <Autocomplete
                    options={CURSOS_MEDIO_TECNICO}
                    value={servidor.medio_tecnico_concluido || null}
                    onChange={(_, newValue) => updateServidor(index, 'medio_tecnico_concluido', newValue ?? '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Digite para buscar o curso"
                        required={!servidor.medio_tecnico_concluido}
                      />
                    )}
                    fullWidth
                    disableClearable={false}
                  />
                </FormControl>
              )}

              {/* Lista condicional para Superior e Pós-graduado */}
              {(getGrauEscolaridadeValue(servidor) === 'Superior' || getGrauEscolaridadeValue(servidor) === 'Pós-graduado') && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Curso Superior</FormLabel>
                  <Autocomplete
                    options={CURSOS_SUPERIOR}
                    value={servidor.superior_concluido || null}
                    onChange={(_, newValue) => updateServidor(index, 'superior_concluido', newValue ?? '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Digite para buscar o curso"
                        required={!servidor.superior_concluido}
                      />
                    )}
                    fullWidth
                    disableClearable={false}
                  />
                </FormControl>
              )}

              {/* Tipo de Vínculo */}
              <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
                <FormLabel sx={{ fontWeight: 'bold', color: '#333' }} component="legend" required>
                  Tipo de Vínculo
                </FormLabel>
                <RadioGroup
                  value={servidor.tipo_vinculo || ''}
                  onChange={(e) => updateServidor(index, 'tipo_vinculo', e.target.value)}
                >
                  {TIPOS_VINCULO.map((tipo) => (
                    <FormControlLabel key={tipo} value={tipo} control={<Radio />} label={tipo} />
                  ))}
                </RadioGroup>
              </FormControl>

              {/* Telefone */}
              <InputComLabel
                valor={mascaraTelefone(servidor.telefone || '')}
                setValor={(valor) => updateServidor(index, 'telefone', mascaraTelefone(valor))}
                labelTexto='Telefone'
                placeholder='(99) 99999-9999'
                maxLength={15}
                required
              />

              <Box sx={{
                mt: 1
              }}>
                {/* Email */}
                <InputComLabel
                  valor={servidor.email || ''}
                  setValor={(valor) => updateServidor(index, 'email', valor)}
                  labelTexto='E-mail'
                  placeholder='exemplo@email.com'
                  type='email'
                  required
                />
                {servidor.email.length > 0 && !regexEmail.test(servidor.email) && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    Formato de email inválido
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </ContainerFormFormik>
      <ButtonForm
        disabled={!podeAvancar}
        onBack={handleVoltar}
        onAdvanced={handleAvancar}
        stepAtual={stepAtual}
        totalSteps={totalSteps}
      />
    </>
  );
};

export default Step6;
